import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Building2, MapPin, Plus, Search, Trash2, Save, X, ShieldCheck, FileText } from "lucide-react";

const TABS = [
  { id: "vendors", label: "Vendor Master", icon: Building2 },
  { id: "sites",   label: "Sites",         icon: MapPin },
];

export default function MastersAdmin() {
  const [tab, setTab] = useState("vendors");
  return (
    <div className="p-6" data-testid="masters-admin-page">
      <div className="mb-6">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Phase 2 · Masters</div>
        <h1 className="text-2xl font-bold text-slate-900">Sites &amp; Vendors</h1>
        <p className="text-sm text-slate-500 mt-1">
          Central catalog of every vendor and HCD site referenced by contracts. New intakes can pick from these lists.
        </p>
      </div>
      <div className="flex gap-1 border-b border-slate-200 mb-6">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`masters-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2 text-sm border-b-2 -mb-px ${
                tab === t.id ? "border-[#005289] text-[#005289] font-semibold" : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>
      {tab === "vendors" && <VendorsPanel />}
      {tab === "sites" && <SitesPanel />}
    </div>
  );
}

/* -------------------- Vendors -------------------- */
const emptyVendor = {
  legal_name: "", dba: "", fein: "", vendor_number: "",
  contact_name: "", contact_email: "", contact_phone: "", address: "",
  w9_on_file: false, coi_on_file: false,
  baa_required: false, baa_on_file: false,
  insurance_requirements: "", diversity_status: "", notes: "", active: true,
};

function VendorsPanel() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // vendor object or null
  const [detail, setDetail] = useState(null); // vendor stats + contracts

  const load = async () => {
    try {
      const { data } = await api.get(`/vendors${search ? `?search=${encodeURIComponent(search)}` : ""}`);
      setRows(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openNew = () => setEditing({ ...emptyVendor });
  const openEdit = (v) => setEditing({ ...emptyVendor, ...v });

  const save = async () => {
    try {
      const body = { ...editing };
      delete body.id; delete body.stats; delete body.legal_name_lower;
      delete body.created_at; delete body.created_by; delete body.created_from;
      delete body.updated_at; delete body.updated_by;
      if (editing.id) await api.patch(`/vendors/${editing.id}`, body);
      else await api.post("/vendors", body);
      toast.success("Vendor saved");
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const deactivate = async (v) => {
    if (!window.confirm(`Deactivate ${v.legal_name}?`)) return;
    try { await api.delete(`/vendors/${v.id}`); toast.success("Deactivated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openDetail = async (v) => {
    try {
      const [d, c] = await Promise.all([
        api.get(`/vendors/${v.id}`),
        api.get(`/vendors/${v.id}/contracts`),
      ]);
      setDetail({ vendor: d.data, contracts: c.data });
    } catch (e) { toast.error("Failed to load vendor detail"); }
  };

  return (
    <div className="space-y-4" data-testid="vendors-panel">
      <div className="flex flex-wrap gap-2 items-center">
        <form onSubmit={e => { e.preventDefault(); load(); }} className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="vendor-search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by legal name, DBA, vendor #, FEIN..."
            className="h-9 pl-9 pr-3 border border-slate-200 rounded-md text-sm w-80"
          />
        </form>
        <button data-testid="vendor-search-btn" onClick={load} className="h-9 px-3 border border-slate-200 rounded-md text-sm hover:bg-slate-50">Search</button>
        <div className="ml-auto text-xs text-slate-500">{rows.length} vendors</div>
        <button data-testid="vendor-new-btn" onClick={openNew} className="h-9 px-4 rounded-md bg-[#005289] text-white text-sm font-semibold flex items-center gap-1">
          <Plus className="w-4 h-4" /> New Vendor
        </button>
      </div>

      <div className="border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-xs text-slate-500 uppercase tracking-wider">
              <th className="text-left px-3 py-2">Legal Name</th>
              <th className="text-left px-3 py-2">FEIN</th>
              <th className="text-left px-3 py-2">Vendor #</th>
              <th className="text-left px-3 py-2">Contact</th>
              <th className="text-left px-3 py-2">BAA</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="w-24"></th>
            </tr>
          </thead>
          <tbody data-testid="vendor-list">
            {rows.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-slate-400">No vendors.</td></tr>}
            {rows.map(v => (
              <tr key={v.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => openDetail(v)}>
                <td className="px-3 py-2">
                  <div className="font-medium">{v.legal_name}</div>
                  {v.dba && <div className="text-xs text-slate-500">DBA: {v.dba}</div>}
                </td>
                <td className="px-3 py-2 font-mono text-xs">{v.fein || "—"}</td>
                <td className="px-3 py-2 font-mono text-xs">{v.vendor_number || "—"}</td>
                <td className="px-3 py-2 text-xs">
                  <div>{v.contact_name || "—"}</div>
                  <div className="text-slate-500">{v.contact_email || ""}</div>
                </td>
                <td className="px-3 py-2 text-xs">
                  {v.baa_required
                    ? (v.baa_on_file ? <span className="text-emerald-700">On file</span> : <span className="text-red-600">Required · MISSING</span>)
                    : <span className="text-slate-400">N/A</span>}
                </td>
                <td className="px-3 py-2">
                  {v.active ? <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Active</span>
                            : <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Inactive</span>}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <button data-testid={`vendor-edit-${v.id}`} onClick={(e) => { e.stopPropagation(); openEdit(v); }} className="text-[#005289] hover:underline text-xs mr-3">Edit</button>
                  {v.active && (
                    <button data-testid={`vendor-del-${v.id}`} onClick={(e) => { e.stopPropagation(); deactivate(v); }} className="text-slate-400 hover:text-red-600">
                      <Trash2 className="w-4 h-4 inline" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && <VendorModal v={editing} setV={setEditing} onSave={save} onClose={() => setEditing(null)} />}
      {detail && <VendorDetailModal detail={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function VendorModal({ v, setV, onSave, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-6 z-50" data-testid="vendor-modal">
      <div className="bg-white rounded-md w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white">
          <div className="font-bold text-slate-900">{v.id ? "Edit Vendor" : "New Vendor"}</div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="vendor-modal-close"><X className="w-4 h-4"/></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-4 text-sm">
          <Field label="Legal Name *"><input data-testid="vm-legal-name" className={inp} value={v.legal_name} onChange={e => setV({...v, legal_name: e.target.value})}/></Field>
          <Field label="DBA"><input className={inp} value={v.dba} onChange={e => setV({...v, dba: e.target.value})}/></Field>
          <Field label="FEIN"><input data-testid="vm-fein" className={inp} value={v.fein} onChange={e => setV({...v, fein: e.target.value})} placeholder="XX-XXXXXXX"/></Field>
          <Field label="Vendor Number"><input className={inp} value={v.vendor_number} onChange={e => setV({...v, vendor_number: e.target.value})}/></Field>
          <Field label="Contact Name"><input className={inp} value={v.contact_name} onChange={e => setV({...v, contact_name: e.target.value})}/></Field>
          <Field label="Contact Email"><input type="email" className={inp} value={v.contact_email} onChange={e => setV({...v, contact_email: e.target.value})}/></Field>
          <Field label="Contact Phone"><input className={inp} value={v.contact_phone} onChange={e => setV({...v, contact_phone: e.target.value})}/></Field>
          <Field label="Diversity Status"><input className={inp} value={v.diversity_status} onChange={e => setV({...v, diversity_status: e.target.value})} placeholder="MBE / WBE / SBE / —"/></Field>
          <div className="col-span-2"><Field label="Address"><input className={inp} value={v.address} onChange={e => setV({...v, address: e.target.value})}/></Field></div>
          <div className="col-span-2"><Field label="Insurance Requirements"><textarea rows={2} className={inp} value={v.insurance_requirements} onChange={e => setV({...v, insurance_requirements: e.target.value})}/></Field></div>

          <div className="col-span-2 grid grid-cols-4 gap-2 pt-2">
            <Toggle label="W-9 on file" checked={v.w9_on_file} onChange={x => setV({...v, w9_on_file: x})} tid="vm-w9"/>
            <Toggle label="COI on file" checked={v.coi_on_file} onChange={x => setV({...v, coi_on_file: x})} tid="vm-coi"/>
            <Toggle label="BAA required" checked={v.baa_required} onChange={x => setV({...v, baa_required: x})} tid="vm-baa-req"/>
            <Toggle label="BAA on file" checked={v.baa_on_file} onChange={x => setV({...v, baa_on_file: x})} tid="vm-baa-file"/>
          </div>

          <div className="col-span-2"><Field label="Notes"><textarea rows={2} className={inp} value={v.notes} onChange={e => setV({...v, notes: e.target.value})}/></Field></div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="h-9 px-4 border border-slate-200 rounded-md text-sm hover:bg-slate-50">Cancel</button>
          <button data-testid="vendor-save-btn" onClick={onSave} className="h-9 px-4 rounded-md bg-[#005289] text-white text-sm font-semibold flex items-center gap-1">
            <Save className="w-4 h-4"/> Save
          </button>
        </div>
      </div>
    </div>
  );
}

function VendorDetailModal({ detail, onClose }) {
  const { vendor, contracts } = detail;
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-6 z-50" data-testid="vendor-detail-modal">
      <div className="bg-white rounded-md w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div>
            <div className="font-bold text-slate-900">{vendor.legal_name}</div>
            {vendor.dba && <div className="text-xs text-slate-500">DBA: {vendor.dba}</div>}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4"/></button>
        </div>
        <div className="p-5 space-y-4 text-sm">
          <div className="grid grid-cols-3 gap-3">
            <Stat label="Contracts"><FileText className="w-3 h-3 inline" /> {vendor.stats?.contract_count ?? 0}</Stat>
            <Stat label="Latest #">{vendor.stats?.latest_contract_number || "—"}</Stat>
            <Stat label="Latest Stage">{vendor.stats?.latest_stage || "—"}</Stat>
          </div>
          <div className="grid grid-cols-2 gap-3 text-xs">
            <KV label="FEIN" v={vendor.fein}/>
            <KV label="Vendor #" v={vendor.vendor_number}/>
            <KV label="Contact" v={vendor.contact_name}/>
            <KV label="Email" v={vendor.contact_email}/>
            <KV label="Phone" v={vendor.contact_phone}/>
            <KV label="Diversity" v={vendor.diversity_status}/>
            <KV label="W-9" v={vendor.w9_on_file ? "On file" : "—"}/>
            <KV label="COI" v={vendor.coi_on_file ? "On file" : "—"}/>
            <KV label="BAA Required" v={vendor.baa_required ? "Yes" : "No"}/>
            <KV label="BAA On File" v={vendor.baa_on_file ? "Yes" : "No"}/>
          </div>
          {contracts.length > 0 && (
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Contracts</div>
              <div className="border border-slate-200 rounded-md overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50">
                    <tr><th className="text-left px-2 py-1">#</th><th className="text-left px-2 py-1">Type</th><th className="text-left px-2 py-1">Dept</th><th className="text-left px-2 py-1">Stage</th></tr>
                  </thead>
                  <tbody>
                    {contracts.map(c => (
                      <tr key={c.id} className="border-t border-slate-100"><td className="px-2 py-1 font-mono">#{c.contract_number}</td><td className="px-2 py-1">{c.contract_type}</td><td className="px-2 py-1">{c.department}</td><td className="px-2 py-1">{c.stage}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* -------------------- Sites -------------------- */
function SitesPanel() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/sites"); setRows(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ code: "", name: "", address: "", site_type: "", active: true });
  const save = async () => {
    try {
      const body = { code: editing.code, name: editing.name, address: editing.address,
                     site_type: editing.site_type, active: editing.active };
      if (editing.id) await api.patch(`/sites/${editing.id}`, body);
      else await api.post("/sites", body);
      toast.success("Site saved"); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const del = async (s) => {
    if (!window.confirm(`Deactivate ${s.name}?`)) return;
    try { await api.delete(`/sites/${s.id}`); toast.success("Deactivated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="sites-panel">
      <div className="flex items-center">
        <div className="text-xs text-slate-500">{rows.length} sites</div>
        <button data-testid="site-new-btn" onClick={openNew} className="ml-auto h-9 px-4 rounded-md bg-[#005289] text-white text-sm font-semibold flex items-center gap-1">
          <Plus className="w-4 h-4" /> New Site
        </button>
      </div>
      <div className="border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-xs text-slate-500 uppercase tracking-wider">
              <th className="text-left px-3 py-2">Code</th>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Type</th>
              <th className="text-left px-3 py-2">Address</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="w-24"></th>
            </tr>
          </thead>
          <tbody data-testid="site-list">
            {rows.map(s => (
              <tr key={s.id} className="border-t border-slate-100">
                <td className="px-3 py-2 font-mono text-xs">{s.code || "—"}</td>
                <td className="px-3 py-2 font-medium">{s.name}</td>
                <td className="px-3 py-2 text-xs">{s.site_type || "—"}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{s.address || "—"}</td>
                <td className="px-3 py-2">
                  {s.active ? <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Active</span>
                            : <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Inactive</span>}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <button data-testid={`site-edit-${s.id}`} onClick={() => setEditing(s)} className="text-[#005289] hover:underline text-xs mr-3">Edit</button>
                  {s.active && <button data-testid={`site-del-${s.id}`} onClick={() => del(s)} className="text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4 inline"/></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-6 z-50" data-testid="site-modal">
          <div className="bg-white rounded-md w-full max-w-lg">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <div className="font-bold">{editing.id ? "Edit Site" : "New Site"}</div>
              <button onClick={() => setEditing(null)}><X className="w-4 h-4 text-slate-400"/></button>
            </div>
            <div className="p-5 space-y-3">
              <Field label="Code"><input data-testid="site-code" className={inp} value={editing.code} onChange={e => setEditing({...editing, code: e.target.value.toUpperCase()})}/></Field>
              <Field label="Name *"><input data-testid="site-name" className={inp} value={editing.name} onChange={e => setEditing({...editing, name: e.target.value})}/></Field>
              <Field label="Type"><input className={inp} value={editing.site_type} onChange={e => setEditing({...editing, site_type: e.target.value})} placeholder="Clinic / Admin / Skilled Nursing / ..."/></Field>
              <Field label="Address"><input className={inp} value={editing.address} onChange={e => setEditing({...editing, address: e.target.value})}/></Field>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.active} onChange={e => setEditing({...editing, active: e.target.checked})}/> Active</label>
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="h-9 px-4 border border-slate-200 rounded-md text-sm">Cancel</button>
              <button data-testid="site-save-btn" onClick={save} className="h-9 px-4 rounded-md bg-[#005289] text-white text-sm font-semibold">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------- small helpers -------------------- */
const inp = "w-full h-9 px-3 border border-slate-200 rounded-md text-sm focus:border-[#005289]";
function Field({ label, children }) {
  return <div><label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">{label}</label>{children}</div>;
}
function Toggle({ label, checked, onChange, tid }) {
  return <label className="flex items-center gap-2 text-xs bg-slate-50 border border-slate-200 rounded-md px-3 h-9 cursor-pointer">
    <input data-testid={tid} type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked)}/> {label}
  </label>;
}
function KV({ label, v }) {
  return <div><div className="text-[10px] uppercase text-slate-400 tracking-wider">{label}</div><div className="text-sm">{v || "—"}</div></div>;
}
function Stat({ label, children }) {
  return <div className="border border-slate-200 rounded-md p-3">
    <div className="text-[10px] uppercase text-slate-400 tracking-wider">{label}</div>
    <div className="text-lg font-semibold text-slate-900 mt-0.5">{children}</div>
  </div>;
}
